import {
  Add,
  NotificationsActive,
  PriorityHigh,
  RemoveCircleOutline,
  WarningAmber,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { usePaginatedSearch } from "hooks/usePaginatedSearch";
import { useEffect, useState } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import ReminderRecipientField, { hasRecipient } from "components/Reminders/ReminderRecipientField";
import { getPaginatedClientData } from "utils/clientService";

const fetchClientPage = ({ page, size, search }) =>
  getPaginatedClientData({ pageIndex: page, pageSize: size, searchText: search });

const REMINDER_TYPES = ["Visa Expiry", "Document Expiry", "Payment Due", "Follow-up", "Custom"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const CHANNELS = ["Email", "SMS", "Push", "WhatsApp"];
const REPEAT_UNITS = ["Days", "Weeks", "Months"];
const VISA_DEFAULT_OFFSETS = [30, 14, 7, 1];

const todayYmd = () => new Date().toISOString().slice(0, 10);
const toYmd = (d) => format(d, "yyyy-MM-dd");
const fromYmd = (d) => new Date(`${d}T00:00:00`);

const makeEscLevel = () => ({
  id: crypto.randomUUID(),
  afterDays: 1,
  notify: "Admin",
});

const createInitialForm = () => ({
  title: "",
  client: null,
  externalClient: null,
  type: "Follow-up",
  dueDate: todayYmd(),
  priority: "Medium",
  reason: "",
  description: "",
  reminderDates: [],
  repeat: { enabled: false, interval: 1, unit: "Days" },
  notifications: {
    notifyClient: false,
    notifyAdmin: true,
    channels: ["Email"],
    clientMessage: "",
    adminMessage: "",
  },
  escalation: { enabled: false, levels: [makeEscLevel()] },
});

function FormToggle({ label, checked, onChange }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2">{label}</Typography>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </Stack>
  );
}

const AddReminderDrawer = ({ id, open, handleClose, onSuccess }) => {
  const [, { refetchReminderList }] = useKothar();
  const handleReminderSaved = () => {
    refetchReminderList?.();
    onSuccess?.();
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={handleClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: "92%", lg: "72%" }, height: "100vh" } }}
    >
      <div className="h-screen flex flex-col">
        <div className="flex items-center justify-between border-b border-primary px-4 py-4">
          <Typography variant="h6">Reminder System</Typography>
          <IconButton onClick={handleClose}>
            <IoCloseCircleOutline className="text-4xl" />
          </IconButton>
        </div>
        <DialogContent sx={{ overflowY: "auto", pb: 3 }}>
          <ReminderDashboard id={id} onReminderSaved={handleReminderSaved} />
        </DialogContent>
      </div>
    </Drawer>
  );
};

function ReminderDashboard({ id, onReminderSaved }) {
  const [form, setForm] = useState(createInitialForm);
  const { data: reminderData, isLoading: isLoadingReminder } = useQuery({
    queryKey: ["reminder", id],
    queryFn: () => axios.get(`${API_URL}/client-reminders/${id}`),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (reminderData) {
      const apiData = reminderData?.data?.data;
      const defaults = createInitialForm();
      setForm({
        ...defaults,
        ...apiData,
        client: apiData?.client ? { id: apiData.client, name: apiData.clientName || "" } : null,
        externalClient:
          !apiData?.client && apiData?.clientEmail
            ? {
                name: apiData.clientName || "",
                email: apiData.clientEmail,
                phone: apiData.clientPhoneNumber || "",
              }
            : null,
        notifications: { ...defaults.notifications, ...(apiData?.notifications || {}) },
        repeat: { ...defaults.repeat, ...(apiData?.repeat || {}) },
        escalation: { ...defaults.escalation, ...(apiData?.escalation || {}) },
      });
    }
  }, [reminderData]);
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setNested = (key, nestedKey, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: { ...prev[key], [nestedKey]: value },
    }));

  const setReminderDates = (value) => setForm((prev) => ({ ...prev, reminderDates: value }));

  const addEscalationLevel = () =>
    setForm((prev) => ({
      ...prev,
      escalation: { ...prev.escalation, levels: [...prev.escalation.levels, makeEscLevel()] },
    }));

  const removeEscalationLevel = (id) =>
    setForm((prev) => ({
      ...prev,
      escalation: {
        ...prev.escalation,
        levels: prev.escalation.levels.filter((l) => l.id !== id),
      },
    }));

  const updateEscalationLevel = (id, key, value) =>
    setForm((prev) => ({
      ...prev,
      escalation: {
        ...prev.escalation,
        levels: prev.escalation.levels.map((l) => (l.id === id ? { ...l, [key]: value } : l)),
      },
    }));

  const resetForm = () => setForm(createInitialForm());

  const isEditing = Boolean(id);

  const mutateReminder = async (payload) => {
    const res = isEditing
      ? await axios.put(`${API_URL}/client-reminders/${id}`, payload)
      : await axios.post(`${API_URL}/client-reminders`, payload);
    return res.data?.data || res.data;
  };

  const { mutate, isLoading } = useMutation(mutateReminder, {
    onSuccess() {
      toast.success(isEditing ? "Reminder updated" : "Reminder created");
      onReminderSaved?.();
      // Only a create leaves the drawer ready for the next one. Blanking an edit would throw away
      // what the user is looking at if the drawer stays open.
      if (!isEditing) resetForm();
    },
    onError(error) {
      const reason = error?.response?.data?.errorMessage || error?.response?.data?.message;
      toast.error(
        reason || (isEditing ? "Failed to update reminder" : "Failed to create reminder")
      );
    },
  });

  const submitForm = () => {
    if (!form.title.trim() || !form.dueDate) {
      toast.error("Title and Due Date are required");
      return;
    }
    if (!hasRecipient(form.client, form.externalClient)) {
      toast.error("Select a client, or add the name and email of someone not in the system");
      return;
    }
    const { externalClient, ...rest } = form;
    mutate({
      ...rest,
      client: form.client?.id ?? null,
      clientName: form.client ? null : externalClient?.name ?? null,
      clientEmail: form.client ? null : externalClient?.email ?? null,
      clientPhoneNumber: form.client ? null : externalClient?.phone ?? null,
    });
  };

  return (
    <Stack spacing={2}>
      <ReminderForm
        id={id}
        form={form}
        setField={setField}
        setNested={setNested}
        setReminderDates={setReminderDates}
        addEscalationLevel={addEscalationLevel}
        removeEscalationLevel={removeEscalationLevel}
        updateEscalationLevel={updateEscalationLevel}
      />

      <DialogActions sx={{ px: 0, pt: 0, justifyContent: "flex-end" }}>
        <Button onClick={resetForm}>Reset</Button>
        <Button
          variant="contained"
          onClick={submitForm}
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : <NotificationsActive />
          }
        >
          {isLoading ? "Saving..." : isEditing ? "Save Changes" : "Add Reminder"}
        </Button>
      </DialogActions>
    </Stack>
  );
}

function ReminderForm({
  id,
  form,
  setField,
  setNested,
  setReminderDates,
  addEscalationLevel,
  removeEscalationLevel,
  updateEscalationLevel,
}) {
  const {
    inputValue: clientSearchInput,
    setInputValue: setClientSearchInput,
    options: clientOptions,
    loading: isClientOptionsLoading,
    listboxProps: clientListboxProps,
  } = usePaginatedSearch(fetchClientPage);
  const clientAutocompleteOptions =
    form.client?.id && !clientOptions.some((c) => c.id === form.client.id)
      ? [form.client, ...clientOptions]
      : clientOptions;

  useEffect(() => {
    setClientSearchInput(form.client?.name || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client?.id]);

  const regenerateVisaSchedule = (dueDate) => {
    const due = fromYmd(dueDate);
    const schedule = VISA_DEFAULT_OFFSETS.map((days) => toYmd(addDays(due, -days))).filter(
      (d) => differenceInCalendarDays(fromYmd(d), new Date()) > 0
    );
    setReminderDates(schedule);
  };

  const handleTypeChange = (value) => {
    setField("type", value);
    if (value === "Visa Expiry") regenerateVisaSchedule(form.dueDate);
  };

  const handleDueDateChange = (value) => {
    setField("dueDate", value);
    if (form.type === "Visa Expiry") regenerateVisaSchedule(value);
  };

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2.5 }}>
      <Stack spacing={2}>
        <TextField
          label="Title *"
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          fullWidth
        />
        <ReminderRecipientField
          client={form.client}
          external={form.externalClient}
          onClientChange={(v) => setField("client", v)}
          onExternalChange={(v) => setField("externalClient", v)}
          options={clientAutocompleteOptions}
          loading={isClientOptionsLoading}
          inputValue={clientSearchInput}
          onInputChange={setClientSearchInput}
          listboxProps={clientListboxProps}
        />
        <TextField
          select
          label="Type"
          value={form.type}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {REMINDER_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          label="Due Date *"
          InputLabelProps={{ shrink: true }}
          value={form.dueDate}
          onChange={(e) => handleDueDateChange(e.target.value)}
        />
        <TextField
          select
          label="Priority"
          value={form.priority}
          onChange={(e) => setField("priority", e.target.value)}
        >
          {PRIORITIES.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Reason for Reminder"
          multiline
          minRows={2}
          value={form.reason}
          onChange={(e) => setField("reason", e.target.value)}
        />
        <TextField
          label="Description"
          multiline
          minRows={3}
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
        />

        <Divider />

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography fontWeight={600}>Reminder Schedule</Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => setReminderDates([...form.reminderDates, form.dueDate])}
            >
              Add date
            </Button>
          </Stack>
          {form.type === "Visa Expiry" && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Schedule auto-generated: 30, 14, 7, 1 days before due date.
            </Alert>
          )}
          <Stack spacing={1}>
            {form?.reminderDates?.map((d, idx) => (
              <Stack key={`${d}-${idx}`} direction="row" spacing={1} alignItems="center">
                <TextField
                  type="date"
                  size="small"
                  value={d}
                  fullWidth
                  onChange={(e) => {
                    const next = [...form.reminderDates];
                    next[idx] = e.target.value;
                    setReminderDates(next);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <IconButton
                  color="error"
                  onClick={() => setReminderDates(form?.reminderDates?.filter((_, i) => i !== idx))}
                >
                  <RemoveCircleOutline />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={600}>Repeat Reminder</Typography>
            <Switch
              checked={form.repeat.enabled}
              onChange={(e) => setNested("repeat", "enabled", e.target.checked)}
            />
          </Stack>
          {form.repeat.enabled && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={1}>
              <TextField
                type="number"
                label="Interval"
                value={form.repeat.interval}
                onChange={(e) => setNested("repeat", "interval", Number(e.target.value) || 1)}
                sx={{ flex: 1 }}
              />
              <TextField
                select
                label="Unit"
                value={form.repeat.unit}
                onChange={(e) => setNested("repeat", "unit", e.target.value)}
                sx={{ flex: 1 }}
              >
                {REPEAT_UNITS.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography fontWeight={600} mb={1}>
            Notification Settings
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormToggle
              label="Notify Client"
              checked={form.notifications.notifyClient}
              onChange={(v) => setNested("notifications", "notifyClient", v)}
            />
            <FormToggle
              label="Notify Admin"
              checked={form.notifications.notifyAdmin}
              onChange={(v) => setNested("notifications", "notifyAdmin", v)}
            />
          </Stack>
          <Autocomplete
            multiple
            options={CHANNELS}
            value={form.notifications?.channels || []}
            onChange={(_, v) => setNested("notifications", "channels", v)}
            renderInput={(params) => <TextField {...params} label="Channels" />}
            sx={{ mt: 1.5 }}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={1.5}>
            <TextField
              label="Client message (optional)"
              multiline
              minRows={2}
              sx={{ flex: 1 }}
              value={form.notifications.clientMessage}
              onChange={(e) => setNested("notifications", "clientMessage", e.target.value)}
            />
            <TextField
              label="Admin message (optional)"
              multiline
              minRows={2}
              sx={{ flex: 1 }}
              value={form.notifications.adminMessage}
              onChange={(e) => setNested("notifications", "adminMessage", e.target.value)}
            />
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={600}>Enable Escalation</Typography>
            <Switch
              checked={form.escalation.enabled}
              onChange={(e) => setNested("escalation", "enabled", e.target.checked)}
            />
          </Stack>
          {form.escalation.enabled && (
            <Stack spacing={1.2} mt={1.5}>
              {form.escalation.levels.map((lvl, idx) => (
                <Stack
                  key={lvl.id}
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.2}
                  alignItems="center"
                >
                  <TextField
                    type="number"
                    label={`After X days (Level ${idx + 1})`}
                    value={lvl.afterDays}
                    onChange={(e) =>
                      updateEscalationLevel(lvl.id, "afterDays", Number(e.target.value) || 1)
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    label="Notify"
                    value={lvl.notify}
                    onChange={(e) => updateEscalationLevel(lvl.id, "notify", e.target.value)}
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Manager">Manager</MenuItem>
                  </TextField>
                  <IconButton color="error" onClick={() => removeEscalationLevel(lvl.id)}>
                    <RemoveCircleOutline />
                  </IconButton>
                </Stack>
              ))}
              <Button variant="text" startIcon={<Add />} onClick={addEscalationLevel}>
                Add Escalation Level
              </Button>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
export default AddReminderDrawer;
